import { useRecoilState, useRecoilValue } from "recoil";
import {
  artistListState,
  deviceListState,
  pushNotificationLogListState,
  songRequestListState,
} from "../atom/atom";
import { SongRequest, Status } from "../types/type";
import { useEffect, useState } from "react";
import {
  updateMakerUserId,
  updateSongRequestStatus,
  updateSongRequestToComplete,
} from "../api/update.api";
import { sendPushNotification } from "../api/common.api";
import { formatDate, copyTextToClipboard } from "../tools/tools";
import dayjs from "dayjs";
import "./styles/SongRequestByUserPage.css";
import CommonButton from "../components/CommonButton";
import {
  fetchAndSetData,
  fetchDeviceList,
  fetchPushNotificationLogList,
  fetchSongRequestList,
} from "../api/fetch.api";

function SongRequestByUserPage() {
  const [songRequestList, setSongRequestList] =
    useRecoilState(songRequestListState);

  const [pushNotificationLogList, setPushNotificationLogList] = useRecoilState(
    pushNotificationLogListState
  );

  // const { userIdToDatesMap } = useMappingUserPushLog();
  // console.log("userIdToDatesMap", userIdToDatesMap);

  const totalArtistList = useRecoilValue(artistListState);
  const [groupedSongRequestList, setGroupedSongRequestList] = useState<{
    [key: string]: SongRequest[];
  }>({});
  const [selectedArtist, setSelectedArtist] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedFirstRequestDate, setSelectedFirstRequestDate] = useState(
    dayjs()
  );
  const [isDateFilterOn, setIsDateFilterOn] = useState(false);
  const [isFirstRequestDateFilterOn, setIsFirstRequestDateFilterOn] =
    useState(false);
  const [completeCountFilter, setCompleteCountFilter] = useState<number | null>(
    null
  );
  const [deviceList, setDeviceList] = useRecoilState(deviceListState);
  const [songIdInputs, setSongIdInputs] = useState<{ [key: string]: string }>(
    {}
  );
  const [editingStatus, setEditingStatus] = useState<string | null>(null);

  const handleSongIdInputChange = (
    songRequestId: string,
    newSongId: string
  ) => {
    setSongIdInputs((prevInputs) => ({
      ...prevInputs,
      [songRequestId]: newSongId,
    }));
  };
  //
  const handleSongIdUpdate = async (
    songRequest: SongRequest,
    fcmToken: string,
    mode: string
  ) => {
    try {
      const newSongId = songIdInputs[songRequest.songRequestId];
      await updateSongRequestToComplete({
        songRequestId: songRequest.songRequestId,
        userId: songRequest.userId,
        newSongId: newSongId,
      });

      setSongRequestList((prevList) =>
        prevList.map((request) =>
          request.songRequestId === songRequest.songRequestId
            ? { ...request, songId: newSongId, status: Status.COMPLETE }
            : request
        )
      );
      setSongIdInputs((prevInputs) => {
        const { [songRequest.songRequestId]: _, ...rest } = prevInputs;
        return rest;
      });

      const token = fcmToken; // Assuming userId is the token; replace with actual token if necessary
      const notificationBody = {
        title: "제조 완료", // Title in Korean
        body: "내 노래가 제조되었어요. 지금 바로 들어보세요!", // Content in Korean
        tokenList: [token], // Assuming token is needed in an array
        data: {
          songTitle: songRequest.songTitle,
          screen: "MyBrewingList", // Adjust screen as needed
          type: "제조완료",
          songId: newSongId,
        },
      };

      await sendPushNotification(notificationBody);

      if (mode === "maker") {
        updateMakerUserId(newSongId, songRequest.userName);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchAndSetData(fetchSongRequestList, setSongRequestList);
    fetchAndSetData(fetchDeviceList, setDeviceList);
    fetchAndSetData(fetchPushNotificationLogList, setPushNotificationLogList);
  }, []);

  useEffect(() => {
    // Group requests by user
    let groupedByUser = songRequestList.reduce((acc, request) => {
      const key = `${request.userId}-${request.userName}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(request);
      return acc;
    }, {} as { [key: string]: SongRequest[] });

    // Apply completeCountFilter
    if (completeCountFilter !== null) {
      groupedByUser = Object.fromEntries(
        Object.entries(groupedByUser).filter(([key, requests]) => {
          const uniqueSongRequests = new Set<string>();

          const completeAndExistingCount = requests.reduce((count, request) => {
            if (
              request.status === Status.COMPLETE ||
              request.status === Status.EXISTING
            ) {
              const songKey = `${request.songTitle}-${request.artistName}`;
              if (!uniqueSongRequests.has(songKey)) {
                uniqueSongRequests.add(songKey);
                return count + 1;
              }
            }
            return count;
          }, 0);

          return (
            (completeCountFilter === 3 && completeAndExistingCount >= 3) ||
            completeAndExistingCount === completeCountFilter
          );
        })
      );
    }

    // Flatten the filtered requests
    let updatedList = Object.values(groupedByUser).flat();

    // Apply additional filters (artist, status, date)
    if (selectedArtist === "others") {
      const artistNames = totalArtistList.map((artist) => artist.artistName);
      updatedList = updatedList.filter(
        (songRequest) => !artistNames.includes(songRequest.artistName)
      );
    } else if (selectedArtist) {
      updatedList = updatedList.filter(
        (songRequest) => songRequest.artistName === selectedArtist
      );
    }

    if (filterStatus === Status.COMPLETE) {
      updatedList = updatedList.filter(
        (songRequest) => songRequest.status === Status.COMPLETE
      );
    } else if (filterStatus === Status.PENDING) {
      updatedList = updatedList.filter(
        (songRequest) => songRequest.status === Status.PENDING
      );
    } else if (filterStatus === Status.EXISTING) {
      updatedList = updatedList.filter(
        (songrequest) => songrequest.status === Status.EXISTING
      );
    }

    if (isDateFilterOn) {
      updatedList = updatedList.filter((songRequest) =>
        dayjs(songRequest.requestAt.toDate()).isSame(selectedDate, "day")
      );
    }

    if (isFirstRequestDateFilterOn) {
      // Filter by user's first song request date
      updatedList = updatedList.filter((songRequest) => {
        const userFirstRequestDate = groupedByUser[
          `${songRequest.userId}-${songRequest.userName}`
        ]
          .map((request) => request.requestAt.toDate())
          .sort((a, b) => a - b)[0];

        return dayjs(userFirstRequestDate).isAfter(
          selectedFirstRequestDate,
          "day"
        );
      });
    }

    // Regroup the filtered requests
    const regroupedRequestList = updatedList.reduce((acc, request) => {
      const key = `${request.userId}-${request.userName}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(request);
      acc[key].sort((a, b) => b.requestAt.toDate() - a.requestAt.toDate());
      return acc;
    }, {} as { [key: string]: SongRequest[] });

    setGroupedSongRequestList(regroupedRequestList);
  }, [
    selectedArtist,
    songRequestList,
    totalArtistList,
    filterStatus,
    selectedDate,
    isDateFilterOn,
    completeCountFilter,
    isFirstRequestDateFilterOn,
    selectedFirstRequestDate,
  ]);

  const calculateStatusCounts = (requests: SongRequest[]) => {
    const uniqueSongs = new Map<string, Status>();

    requests.forEach((request) => {
      const songKey = `${request.songTitle}-${request.artistName}`;
      const currentStatus = uniqueSongs.get(songKey);

      if (!currentStatus) {
        // 아직 상태가 설정되지 않은 경우
        uniqueSongs.set(songKey, request.status);
      } else {
        // 기존 상태가 있으면 우선순위에 따라 업데이트
        if (request.status === Status.EXISTING) {
          uniqueSongs.set(songKey, Status.EXISTING);
        } else if (
          currentStatus !== Status.EXISTING &&
          request.status === Status.COMPLETE
        ) {
          uniqueSongs.set(songKey, Status.COMPLETE);
        }
      }
    });

    // 상태별로 카운트 계산
    const counts = {
      existing: 0,
      pending: 0,
      completed: 0,
    };

    uniqueSongs.forEach((status) => {
      if (status === Status.EXISTING) {
        counts.existing += 1;
      } else if (status === Status.COMPLETE) {
        counts.completed += 1;
      } else if (status === Status.PENDING) {
        counts.pending += 1;
      }
    });

    return counts;
  };

  const userStatusCounts = Object.entries(
    songRequestList.reduce((acc, request) => {
      const key = `${request.userId}-${request.userName}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(request);
      return acc;
    }, {} as { [key: string]: SongRequest[] })
  ).reduce((acc, [key, requests]) => {
    acc[key] = calculateStatusCounts(requests);
    return acc;
  }, {} as { [key: string]: { existing: number; pending: number; completed: number } });

  const handleDateChange = (direction: "prev" | "next") => {
    setSelectedDate((prevDate) =>
      direction === "prev"
        ? prevDate.subtract(1, "day")
        : prevDate.add(1, "day")
    );
  };

  const handleFirstRequestDateChange = (direction: "prev" | "next") => {
    setSelectedFirstRequestDate((prevDate) =>
      direction === "prev"
        ? prevDate.subtract(1, "day")
        : prevDate.add(1, "day")
    );
  };

  const handleStatusChange = async (
    songRequestId: string,
    userId: string,
    newStatus: Status
  ) => {
    try {
      await updateSongRequestStatus(songRequestId, userId, newStatus);

      setSongRequestList((prevList) =>
        prevList.map((request) =>
          request.songRequestId === songRequestId
            ? { ...request, status: newStatus }
            : request
        )
      );
    } catch (error) {
      console.error("Error updating status: ", error);
    }
  };

  const handleStatusClick = (songRequestId: string) => {
    setEditingStatus(songRequestId);
  };

  return (
    <>
      <div className="container">
        <div className="sort-group">
          <div className="sort-group-title">완료 갯수</div>
          <CommonButton
            onClick={() => setCompleteCountFilter(0)}
            selected={completeCountFilter === 0}
          >
            0
          </CommonButton>
          <CommonButton
            onClick={() => setCompleteCountFilter(1)}
            selected={completeCountFilter === 1}
          >
            1
          </CommonButton>
          <CommonButton
            onClick={() => setCompleteCountFilter(2)}
            selected={completeCountFilter === 2}
          >
            2
          </CommonButton>
          <CommonButton
            onClick={() => setCompleteCountFilter(3)}
            selected={completeCountFilter === 3}
          >
            3
          </CommonButton>
          <CommonButton
            onClick={() => setCompleteCountFilter(null)}
            selected={completeCountFilter === null}
          >
            전체
          </CommonButton>
        </div>
        <div className="sort-group">
          <div className="sort-group-title">가수별 정렬</div>
          <CommonButton
            onClick={() => setSelectedArtist(null)}
            selected={selectedArtist === null}
          >
            전체
          </CommonButton>
          {totalArtistList.map((artist) => (
            <CommonButton
              key={artist.artistId}
              onClick={() => setSelectedArtist(artist.artistName)}
              selected={selectedArtist === artist.artistName}
            >
              {artist.artistName}
            </CommonButton>
          ))}
          <CommonButton
            onClick={() => setSelectedArtist("others")}
            selected={selectedArtist === "others"}
          >
            Others
          </CommonButton>
        </div>
        <div className="sort-group">
          날짜별 정렬
          <input
            type="checkbox"
            checked={isDateFilterOn}
            onChange={(e) => setIsDateFilterOn(e.target.checked)}
          />
          <div className="date-selector">
            <button
              onClick={() => handleDateChange("prev")}
              disabled={!isDateFilterOn}
            >
              ◀
            </button>
            <span>{selectedDate.format("YYYY-MM-DD")}</span>
            <button
              onClick={() => handleDateChange("next")}
              disabled={!isDateFilterOn}
            >
              ▶
            </button>
          </div>
        </div>
        <div className="sort-group">
          <div className="sort-group-title">첫 신청곡 날짜 필터</div>
          <input
            type="checkbox"
            checked={isFirstRequestDateFilterOn}
            onChange={(e) => setIsFirstRequestDateFilterOn(e.target.checked)}
          />
          <div className="date-selector">
            <button
              onClick={() => handleFirstRequestDateChange("prev")}
              disabled={!isFirstRequestDateFilterOn}
            >
              ◀
            </button>
            <span>{selectedFirstRequestDate.format("YYYY-MM-DD")}</span>
            <button
              onClick={() => handleFirstRequestDateChange("next")}
              disabled={!isFirstRequestDateFilterOn}
            >
              ▶
            </button>
          </div>
        </div>
        <div className="sort-group">
          Status별 정렬
          <CommonButton
            onClick={() => setFilterStatus("all")}
            selected={filterStatus === "all"}
          >
            전체
          </CommonButton>
          <CommonButton
            onClick={() => setFilterStatus(Status.PENDING)}
            selected={filterStatus === Status.PENDING}
          >
            Pending
          </CommonButton>
          <CommonButton
            onClick={() => setFilterStatus(Status.COMPLETE)}
            selected={filterStatus === Status.COMPLETE}
          >
            Complete
          </CommonButton>
          <CommonButton
            onClick={() => setFilterStatus(Status.EXISTING)}
            selected={filterStatus === Status.EXISTING}
          >
            Existing
          </CommonButton>
        </div>
      </div>

      {Object.entries(groupedSongRequestList).map(([key, requests]) => {
        const latestRequest = requests.reduce((latest, request) =>
          latest.requestAt.toDate() > request.requestAt.toDate()
            ? latest
            : request
        );

        const matchingDevice =
          deviceList.find(
            (device) =>
              device.fcmToken === latestRequest.userToken &&
              device.userName === latestRequest.userName
          ) ||
          deviceList.find(
            (device) => device.fcmToken === latestRequest.userToken
          ) ||
          deviceList.find(
            (device) => device.userName === latestRequest.userName
          );

        const { existing, pending, completed } = userStatusCounts[key];

        return (
          <div key={key} className="user-request-container">
            <div className="user-info">
              <p>
                name: {requests[0].userName} / {requests[0].userEmail}
              </p>
              <p style={{ cursor: "pointer" }}>
                Token:{" "}
                {requests.reduce((latest, request) =>
                  latest.requestAt.toDate() > request.requestAt.toDate()
                    ? latest
                    : request
                ).userToken ? (
                  <span
                    onClick={() =>
                      copyTextToClipboard(
                        requests.reduce((latest, request) =>
                          latest.requestAt.toDate() > request.requestAt.toDate()
                            ? latest
                            : request
                        ).userToken
                      )
                    }
                  >
                    {requests
                      .reduce((latest, request) =>
                        latest.requestAt.toDate() > request.requestAt.toDate()
                          ? latest
                          : request
                      )
                      .userToken.substring(0, 10)}
                    ...
                  </span>
                ) : (
                  ""
                )}
              </p>
              {matchingDevice && (
                <p
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    copyTextToClipboard(matchingDevice.hashedDeviceId);
                  }}
                >
                  {matchingDevice.hashedDeviceId}
                </p>
              )}
              <p>
                <span>Ex: {existing}</span>
                {"  /  "}Co:{completed}
                {"  /  "}
                <span style={{ color: "#e74c3c" }}>Pe: {pending}</span>
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "row" }}>
              {requests.map((songRequest) => (
                <div key={songRequest.songRequestId} className="song-request">
                  <p>
                    {songRequest.artistName} - {songRequest.songTitle}
                  </p>
                  <p>
                    Status:
                    {editingStatus === songRequest.songRequestId ? (
                      <select
                        value={songRequest.status}
                        onChange={(e) =>
                          handleStatusChange(
                            songRequest.songRequestId,
                            songRequest.userId,
                            e.target.value as Status
                          )
                        }
                        onBlur={() => setEditingStatus(null)}
                      >
                        <option value={Status.PENDING}>Pending</option>
                        <option value={Status.COMPLETE}>Complete</option>
                        <option value={Status.EXISTING}>Existing</option>
                        <option value={Status.FAILED}>Failed</option>
                        <option value={Status.ERROR}>Error</option>
                      </select>
                    ) : (
                      <span
                        onClick={() =>
                          handleStatusClick(songRequest.songRequestId)
                        }
                      >
                        {songRequest.status}
                      </span>
                    )}
                  </p>
                  <p>날짜: {formatDate(songRequest.requestAt)}</p>
                  <p>
                    Song ID:{" "}
                    {songRequest.songId ? (
                      songRequest.songId
                    ) : (
                      <>
                        <input
                          type="text"
                          value={songIdInputs[songRequest.songRequestId] || ""}
                          onChange={(e) =>
                            handleSongIdInputChange(
                              songRequest.songRequestId,
                              e.target.value
                            )
                          }
                          placeholder="Enter Song ID"
                        />
                        <button
                          onClick={() =>
                            handleSongIdUpdate(
                              songRequest,
                              matchingDevice?.fcmToken || "",
                              "maker"
                            )
                          }
                        >
                          메이커
                        </button>
                        <button
                          onClick={() =>
                            handleSongIdUpdate(
                              songRequest,
                              matchingDevice?.fcmToken || "",
                              "general"
                            )
                          }
                        >
                          일반
                        </button>
                      </>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

export default SongRequestByUserPage;
